import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from '@/components/FullCalendar';
import { getCalendarEvents } from '@/api/calendar';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import {
	setEvents,
	setActiveDate,
	selectCalendarEvents,
	selectActiveDateCalendarStats,
	selectActiveDate,
	selectCalendarStats,
} from '@/store/calendarEvents/slice';
import { useMutation } from '@tanstack/react-query';
import { getSelectedAgentId } from '@/store/account/selector';
import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import './CalendarEvents.scss';
import { CalendarEvent } from 'src/types/TCalendarEvents';
import { MultiAgentDatePickerModeEnum } from 'src/enums/EMultiAgentDatePicker';
import { CalendarEventsHeader } from '@/components/CalendarEvents/CalendarEventsHeader';
import { CalendarEventsToolBar } from '@/components/CalendarEvents/CalendarEventsToolBar';
import { CalendarEventsNav } from '@/components/CalendarEvents/CalendarEventsNav';
import { CalendarAppointmentsList } from '@/components/CalendarEvents/CalendarAppointmentsList';
import { CalendarStatsCard } from '@/components/CalendarEvents/CalendarStatsCard';
import { CustomModal } from '@/components/CustomModal';
import { MODAL_IDS } from 'src/enums/EModal';
import { getModalData } from '@/store/modal/selector';
import { closeModal } from '@/store/modal/slice';

export const CalendarEvents: React.FC = () => {
	const dispatch = useAppDispatch();
	const events = useAppSelector(selectCalendarEvents);
	const activeDate = useAppSelector(selectActiveDate);
	const stats = useAppSelector(selectCalendarStats);
	const activeDateEvents = useAppSelector(selectActiveDateCalendarStats);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const calendarRef = useRef<any>(null);
	const [currentView, setCurrentView] = useState('dayGridMonth');
	const [pickerMode, setPickerMode] = useState<MultiAgentDatePickerModeEnum>(
		MultiAgentDatePickerModeEnum.SINGLE,
	);
	const [dateValue, setDateValue] = useState<Dayjs | null>(dayjs());
	const [rangeValue, setRangeValue] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
	const [showDatePicker, setShowDatePicker] = useState(false);

	const { mutate: fetchEvents } = useMutation({
		mutationFn: (params: { startDate?: string; endDate?: string; agentId: string }) =>
			getCalendarEvents(params),
		onSuccess: (data) => {
			const mappedEvents = (data?.data || []).map((event: CalendarEvent) => {
				const start = event.event_timestamp;
				let end = start;
				if (event.duration_minutes) {
					end = dayjs(start).add(event.duration_minutes, 'minute').toISOString();
				}
				return {
					id: event.SK || event.PK + start,
					title: event.title,
					start,
					end,
					status: event.status,
					extendedProps: { ...event },
				};
			});
			dispatch(setEvents(mappedEvents));
		},
	});

	useEffect(() => {
		if (!selectedAgentId) {
			return;
		}
		fetchEvents({ agentId: selectedAgentId });
	}, [selectedAgentId, fetchEvents]);

	useEffect(() => {
		if (events.length > 0) {
			dispatch(setActiveDate(moment().format('YYYY-MM-DD')));
		}
	}, [events]);

	const handleEventClick = (arg: any) => {
		dispatch(setActiveDate(moment(arg.date).format('YYYY-MM-DD')));
		setCurrentView(arg.view.type);
	};

	const handlePrev = () => calendarRef.current?.getApi().prev();
	const handleNext = () => calendarRef.current?.getApi().next();
	const handleToday = () => calendarRef.current?.getApi().today();
	const handleViewChange = (view: string) => {
		calendarRef.current?.getApi().changeView(view);
		setCurrentView(view);
	};

	const handleDatePickerOk = (clear: boolean = false) => {
		if (!selectedAgentId) {
			return;
		}
		if (clear) {
			fetchEvents({
				agentId: selectedAgentId,
			});
			return;
		}
		if (pickerMode === MultiAgentDatePickerModeEnum.SINGLE && dateValue) {
			fetchEvents({
				agentId: selectedAgentId,
				startDate: dateValue.format('YYYY-MM-DD'),
				endDate: dateValue.format('YYYY-MM-DD'),
			});
		} else if (
			pickerMode === MultiAgentDatePickerModeEnum.RANGE &&
			rangeValue[0] &&
			rangeValue[1]
		) {
			fetchEvents({
				agentId: selectedAgentId,
				startDate: rangeValue[0].format('YYYY-MM-DD'),
				endDate: rangeValue[1].format('YYYY-MM-DD'),
			});
		}
		setShowDatePicker(false);
	};

	const getCurrentTitle = () => {
		return calendarRef.current?.getApi().view?.title || moment().format('MMMM YYYY');
	};

	const modalData = useAppSelector(getModalData) as any;

	const statusColorMap = {
		CONFIRMED: 'green',
		SCHEDULED: 'green',
		RESCHEDULED: 'gold',
		CANCELLED: 'red',
	};
	function formatKey(key: string) {
		return key
			.split('_')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	}
	const renderEvent = (event: any, idx: number) => {
		const extraInfo = event.extendedProps?.extra_display_event_info;
		const extendedProps = event.extendedProps;
		return (
			<div key={idx} className="bg-gray-50 rounded-lg p-4 mb-3 border-l-4 border-violet-400">
				<div className="flex items-center gap-2 mb-1">
					<span className="text-gray-700 font-semibold text-base">
						{dayjs(event.start.toString()).format('h:mm A')}
					</span>
					<span
						className={`ant-tag ant-tag-${statusColorMap[event.status] || 'blue'}`}
						style={{ textTransform: 'capitalize', fontWeight: 600 }}
					>
						{event.status?.toLowerCase()}
					</span>
				</div>
				<div className="font-semibold text-gray-900 mb-1">{event.title}</div>
				{extendedProps?.attendee?.name && (
					<div className="text-gray-700 text-sm">{extendedProps?.attendee.name}</div>
				)}
				{extendedProps?.attendee?.phone_number && (
					<div className="text-gray-500 text-xs">{extendedProps?.attendee.phone_number}</div>
				)}
				<div className="text-gray-400 text-xs mt-1">
					<b>Duration:</b> {extendedProps?.duration_minutes} minutes
				</div>
				{extraInfo && typeof extraInfo === 'object' && (
					<div className="mt-1">
						{Object.entries(extraInfo).map(([key, value]) => {
							const strValue = String(value);
							const truncated = strValue.length > 40 ? strValue.slice(0, 37) + '...' : strValue;
							return (
								<div key={key} className="text-gray-500 text-xs truncate">
									<span className="font-medium text-gray-600">{formatKey(key)}:</span>{' '}
									<span title={strValue}>{truncated}</span>
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="p-4 sm:p-6 md:p-8 calendar-events-container">
			<div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 md:gap-8">
				<div className="flex flex-col min-w-0 order-1 lg:order-1">
					<div className="flex flex-col gap-2 mb-4 md:mb-6 lg:mb-8">
						<div className="w-full">
							<CalendarEventsHeader />
						</div>
						<div className="w-full toolbar-container">
							<div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 items-stretch">
								<CalendarEventsToolBar
									currentView={currentView}
									onViewChange={handleViewChange}
									pickerMode={pickerMode}
									setPickerMode={setPickerMode}
									dateValue={dateValue}
									setDateValue={setDateValue}
									rangeValue={rangeValue}
									setRangeValue={setRangeValue}
									showDatePicker={showDatePicker}
									setShowDatePicker={setShowDatePicker}
									handleDatePickerOk={handleDatePickerOk}
									handleToday={handleToday}
								/>
							</div>
						</div>
					</div>
					<CalendarEventsNav title={getCurrentTitle()} onPrev={handlePrev} onNext={handleNext} />
					<div>
						<Calendar
							ref={calendarRef}
							events={events}
							initialView={currentView}
							headerToolbar={false}
							dateClick={handleEventClick}
						/>
					</div>
					<div className="flex flex-col gap-4 w-full mt-6 lg:hidden">
						<CalendarAppointmentsList events={activeDateEvents} date={activeDate} />
						<CalendarStatsCard stats={stats} month={dayjs(activeDate).format('MMMM YYYY')} />
					</div>
				</div>
				<div className="hidden lg:flex flex-col gap-4 w-full max-w-[400px] mx-auto lg:mx-0 order-2">
					<CalendarAppointmentsList events={activeDateEvents} date={activeDate} />
					<CalendarStatsCard stats={stats} month={dayjs(activeDate).format('MMMM YYYY')} />
				</div>
			</div>
			<CustomModal
				title={`All Appointments for ${modalData && modalData.date ? dayjs(modalData.date).format('dddd, MMM D') : ''}`}
				modalId={MODAL_IDS.CALENDAR_APPOINTMENTS_MODAL}
				footer={null}
				width={500}
				cancelAction={() => dispatch(closeModal())}
			>
				<div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
					{Array.isArray(modalData?.events) && modalData.events.length > 0 ? (
						modalData.events.map(renderEvent)
					) : (
						<div className="text-gray-400 text-sm">No appointments</div>
					)}
				</div>
			</CustomModal>
		</div>
	);
};
